import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Row, Col, Card, Button, Image, Dropdown, Menu, Modal, message, Input, Form, Select } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined, DownloadOutlined } from "@ant-design/icons";
import CustomerMainLayout from "../CustomerMainLayout";
import AiLoader from "../../AiAsserCreate/AiLoader";
import "./InspirationLibrary.scss";
import { ReactComponent as ImgOutline } from '../../../../assets/images/black-large-img-outline.svg';
import { ReactComponent as MoreIcon } from '../../../../icons/more.svg';
import axios from "axios";
import ENVIRONMENT from "../../../../environments";
import { getBaseURL } from "../../../../environments/env";
import DeleteEntityModal from "../../DeleteEntityModal/DeleteEntityModal";
import EditInspirationModal from "./EditInspirationModal";
import CreateProductModal from "../../AiAsserCreate/CreateProductModal";
import { downloadFile } from "../../Utilities";
import { addDownloadIconObserver, showCustomToast } from "../../../../utils/addImageDownload";

const CUSTOMER_USERNAME = localStorage.getItem("username");
const COMPANY_ID = localStorage.getItem("company_id");

const InspirationLibrary = () => {
  const [images, setImages] = useState([]);
  const [displayedImages, setDisplayedImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [deleteImageLoader, setDeleteImageLoader] = useState(false);
  const [itemToEdit, setItemToEdit] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [createInspirationModalVisible, setCreateInspirationModalVisible] = useState(false);
  const [addInspirationLoader, setAddInspirationLoader] = useState(false);
  const [selectedCreatedBy, setSelectedCreatedBy] = useState("all");
  const galleryRef = useRef();
  const pageRef = useRef(1);
  const isDeletingRef = useRef(false);
  const isEditingRef = useRef(false);
  const pageSize = 20;

  // Helper function to get identifier or "Untitled"
  const getIdentifier = useCallback((item) => {
    const identifier = item.text || item.identifier || item.TEXT || item.text_identifier || item.identifier_text || '';
    return identifier.trim();
  }, []);

  // Helper function to generate keywords from inspiration data
  const generateKeywords = useCallback((image) => {
    let keywords = [];

    // Add ID as keyword
    if (image.id) {
      keywords.push(String(image.id).toLowerCase());
    }

    // Get identifier and generate keywords from it
    const identifier = getIdentifier(image);
    if (identifier && identifier !== 'Untitled') {
      const identifierKeywords = identifier.replace(/[^a-zA-Z0-9 ]/g, " ").split(" ");
      identifierKeywords.forEach(keyword => {
        if (keyword.length > 1 && !keywords.includes(keyword.toLowerCase())) {
          keywords.push(keyword.toLowerCase());
        }
      });
    }

    return keywords;
  }, [getIdentifier]);

  const fetchInspirationImages = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        action: 'get_all_inspirations',
        username: CUSTOMER_USERNAME,
        company_id: COMPANY_ID
      };

      const response = await axios.post(ENVIRONMENT.AI_MODEL_HELPER, payload);

      if (response.data && response.data.result === 'success' && Array.isArray(response.data.data)) {
        const baseURL = getBaseURL();
        const processedImages = response.data.data.map((image, index) => {
          const baseImageUrl = baseURL + image.image_uri;
          const thumbnailUrl = image.thumbnail ? baseURL + image.thumbnail : null;
          const imageData = {
            src: baseImageUrl,
            width: image.width,
            height: image.height,
            depth: image.depth,
            id: image.id || index,
            title: `Inspiration ${image.id || index + 1}`,
            created_on: image.created_on,
            image_uri: image.image_uri,
            ...image
          };
          imageData.thumbnail = thumbnailUrl;
          // Generate keywords array from available fields
          imageData.keywords = generateKeywords(imageData);

          return imageData;
        });
        setImages(processedImages);
      } else {
        // Handle empty or unexpected response
        setImages([]);
      }
    } catch (error) {
      console.error("Error fetching inspiration images:", error);
      setImages([]);
    } finally {
      setLoading(false);
    }
  }, [generateKeywords]);

  useEffect(() => {
    fetchInspirationImages();
  }, [fetchInspirationImages]);

  // Get unique usernames (created_by) from inspiration data
  const distinctUsernames = useMemo(() => {
    const uniqueUsernames = new Set(images.map(img => img.created_by || img.username).filter(Boolean));
    return Array.from(uniqueUsernames).sort();
  }, [images]);

  // Filter images based on search value and created by filter
  const filteredImages = useMemo(() => {
    return images.filter(image => {
      // Filter by created by
      if (selectedCreatedBy !== 'all') {
        const imageCreator = image.created_by || image.username;
        if (imageCreator !== selectedCreatedBy) return false;
      }

      // Filter by search value
      if (!searchValue.trim()) {
        return true;
      }

      const searchLower = searchValue.toLowerCase().trim();
      // Split search query into individual keywords
      const searchKeywords = searchLower.split(/\s+/).filter(k => k.length > 0);

      // If image has keywords array, search in it
      if (image.keywords && Array.isArray(image.keywords)) {
        // Check if any search keyword matches any keyword in the image (OR logic)
        return searchKeywords.some(searchKeyword =>
          image.keywords.some(keyword => keyword.includes(searchKeyword))
        );
      }
      // Fallback to identifier if keywords don't exist
      const identifier = getIdentifier(image).toLowerCase();
      return searchKeywords.some(searchKeyword =>
        identifier.includes(searchKeyword)
      );
    });
  }, [images, searchValue, selectedCreatedBy, getIdentifier]);

  const loadMoreImages = useCallback(() => {
    if (!filteredImages || filteredImages.length === 0) return;

    const startIndex = (pageRef.current - 1) * pageSize;
    const newImages = filteredImages.slice(startIndex, startIndex + pageSize);

    if (newImages.length > 0) {
      setDisplayedImages((prev) => [...prev, ...newImages]);
      pageRef.current += 1;
    }
  }, [filteredImages, pageSize]);

  useEffect(() => {
    setDisplayedImages([]);
    pageRef.current = 1;

    if (filteredImages && filteredImages.length > 0) {
      loadMoreImages();
    }
  }, [filteredImages, loadMoreImages]);

  const handleScroll = useCallback(() => {
    if (!galleryRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = galleryRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      loadMoreImages();
    }
  }, [loadMoreImages]);

  // Auto-fill effect for large screens:
  // After images are displayed, check if the gallery viewport is filled.
  // If not, and there's more to load, trigger another load.
  useEffect(() => {
    // Small timeout to allow Grid layout to settle
    const timer = setTimeout(() => {
      if (galleryRef.current && displayedImages.length > 0) {
        const { scrollHeight, clientHeight } = galleryRef.current;
        const totalAvailable = (filteredImages && filteredImages.length) || 0;
        
        // If scrollable height is not greater than visible height (no scrollbar),
        // and we haven't exhausted the filtered images list, load the next batch.
        if (scrollHeight <= clientHeight + 10 && displayedImages.length < totalAvailable) {
          loadMoreImages();
        }
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [displayedImages.length, filteredImages?.length, loadMoreImages]);

  const handleImageClick = (index, e) => {
    // Prevent opening if clicking on dropdown or menu items
    if (e && (e.target.closest('.ant-dropdown') || e.target.closest('.ant-menu'))) {
      return;
    }
    // Don't open if delete/edit modal is visible or about to be visible, or if delete/edit was just clicked
    if (deleteModalVisible || itemToDelete || isDeletingRef.current || editModalVisible || itemToEdit || isEditingRef.current) {
      return;
    }
    const imageData = displayedImages[index];
    if (imageData) {
      setSelectedImage(imageData);
      // Use setTimeout to ensure Image component is rendered before showing preview
      setTimeout(() => {
        setIsModalVisible(true);
      }, 0);
    }
  };

  const handleDelete = (item) => {
    isDeletingRef.current = true;
    setItemToDelete(item);
    setDeleteModalVisible(true);
    // Reset the flag after a short delay to allow any pending card clicks to be blocked
    setTimeout(() => {
      isDeletingRef.current = false;
    }, 100);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    setDeleteImageLoader(true);

    try {
      const payload = {
        action: 'delete_inspiration',
        inspiration_id: itemToDelete.id,
        username: CUSTOMER_USERNAME,
        company_id: COMPANY_ID
      };

      const response = await axios.post(ENVIRONMENT.AI_MODEL_HELPER, payload);

      if (response.data && response.data.result === 'success') {
        setDeleteImageLoader(false);
        message.success('Inspiration deleted successfully');
        // Remove from displayed images
        setDisplayedImages(prev => prev.filter(img => img.id !== itemToDelete.id));
        setImages(prev => prev.filter(img => img.id !== itemToDelete.id));
        setDeleteModalVisible(false);
        setItemToDelete(null);
        isDeletingRef.current = false;
      } else {
        message.error('Failed to delete inspiration');
      }
    } catch (error) {
      console.error("Error deleting inspiration:", error);
      message.error('Error deleting inspiration');
    }
  };

  const handleEdit = (item) => {
    isEditingRef.current = true;
    setItemToEdit(item);
    setEditModalVisible(true);
    // Reset the flag after a short delay to allow any pending card clicks to be blocked
    setTimeout(() => {
      isEditingRef.current = false;
    }, 100);
  };

  const handleEditCancel = () => {
    setEditModalVisible(false);
    setItemToEdit(null);
    isEditingRef.current = false;
  };

  const handleEditSuccess = (updatedItem) => {
    // Update the item in the images array and regenerate keywords
    setImages(prev => prev.map(img => {
      if (img.id === updatedItem.id) {
        const updated = { ...updatedItem };
        updated.keywords = generateKeywords(updated);
        return updated;
      }
      return img;
    }));
  };

  const handleAddInspirationClick = () => {
    setCreateInspirationModalVisible(true);
  };

  const handleCreateInspirationModalOk = async (data) => {
    try {
      // Extract data from modal
      const { files, name, category, source } = data;

      // If selecting from library, no need to make an API call
      if (source === 'library') {
        setCreateInspirationModalVisible(false);
        message.info('Inspiration from library selected');
        return;
      }

      // Validate that we have the required data
      if (!files || files.length === 0) {
        message.error('Please upload an inspiration image');
        return;
      }

      const file = files[0];

      // Construct the image URL from the uploaded file
      // const imageUrl = "test_files" + "/" + file.uid + "/" + file.name;
      const imageUrl = ENVIRONMENT.getBaseURL() + encodeURIComponent('test_files/' + file.uid + '/' + file.name);

      // Prepare the payload
      const payload = {
        action: 'add_inspiration',
        image_url: imageUrl,
        username: CUSTOMER_USERNAME,
        identifier: name || 'Untitled',
        category: category || '',
        company_id: COMPANY_ID
      };

      // Close modal immediately and show loading
      setCreateInspirationModalVisible(false);
      setAddInspirationLoader(true);

      // Make the API call
      const response = await axios.post(ENVIRONMENT.AI_MODEL_HELPER, payload);

      if (response.data && response.data.result === 'success') {
        message.success('Inspiration added successfully');
        // Refresh the inspirations list
        fetchInspirationImages();
      } else {
        message.error('Failed to add inspiration');
      }
    } catch (error) {
      console.error("Error adding inspiration:", error);
      message.error('Error adding inspiration');
    } finally {
      setAddInspirationLoader(false);
    }
  };

  const handleCreateInspirationModalCancel = () => {
    setCreateInspirationModalVisible(false);
  };

  // Handle bulk inspiration upload
  const handleBulkInspirationUpload = async (data) => {
    const { csvFile, requestName } = data;
    const file_uri = 'test_files/' + csvFile.uid + '/' + csvFile.name;
    
    const payload = {
      action: 'submit_bulk_inspirations',
      csv_file_uri: file_uri,
      bulk_upload_name: requestName,
      company_id: COMPANY_ID,
      username: CUSTOMER_USERNAME
    };
    
    console.log('Bulk inspiration upload payload:', payload);
    
    try {
      const res = await axios.post(ENVIRONMENT.AI_BULK_JOB_CONTROLLER, payload);
      
      // Handle explicit error result
      if (res.data && res.data.result === 'error') {
        let errors = [];
        
        if (Array.isArray(res.data.errors) && res.data.errors.length > 0) {
          // Validation errors list
          errors = res.data.errors.map((err) => ({
            row: err.row || '-',
            message: err.message || err.error || (typeof err === 'string' ? err : JSON.stringify(err))
          }));
        } else {
          // Generic error message
          errors = [{
            row: '-',
            message: res.data.message || 'Unknown error occurred'
          }];
        }
        
        return { success: false, errors: errors };
      }
      
      // Success case
      message.success('Hang tight! New inspirations are being added to your library.');
      setCreateInspirationModalVisible(false);
      // Fetch after 1 minute to allow backend to process
      setTimeout(() => {
        fetchInspirationImages();
      }, 60 * 1000);
      return { success: true };
      
    } catch (error) {
      console.error('Bulk inspiration upload error:', error);
      return {
        success: false,
        errors: [{ row: '-', message: 'Network or server error occurred.' }]
      };
    }
  };

  const handleDownload = (item) => {
    if (item?.src) {
      let extension = item.src.split('.').pop();
      const fileName = getIdentifier(item);
      downloadFile(item.src, fileName, extension);
    }
  };

  // Observer to add download button to image preview
  useEffect(() => {
    const downloadImage = () => {
      if (selectedImage) {
        handleDownload(selectedImage);
      }
    };
    const observer = addDownloadIconObserver(downloadImage);
    return () => observer.disconnect();
  }, [selectedImage]);

  const getMenuOptions = (item) => {
    return (
      <Menu>
        <Menu.Item key="edit" icon={<EditOutlined />} onClick={() => handleEdit(item)}>
          <span className="manrope f-14">Edit</span>
        </Menu.Item>
        <Menu.Item key="delete" icon={<DeleteOutlined />} onClick={() => handleDelete(item)}>
          <span className="manrope f-14">Delete</span>
        </Menu.Item>
        <Menu.Item key="download" icon={<DownloadOutlined />} onClick={() => handleDownload(item)}>
          <span className="manrope f-14">Download</span>
        </Menu.Item>
      </Menu>
    );
  };


  if (loading) {
    return (
      <CustomerMainLayout selected="36" hide_scroll={true}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 200px)', width: '100%' }}>
          <AiLoader />
        </div>
      </CustomerMainLayout>
    );
  }

  if (!displayedImages.length && !loading) {
    return (
      <CustomerMainLayout selected="36">
        <div style={{ position: 'relative', padding: "20px" }}>
          {addInspirationLoader && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              zIndex: 1000
            }}>
              <AiLoader />
            </div>
          )}
          <div style={{ padding: "20px" }}>
          <Row className="justify-space-between" style={{ marginBottom: "20px" }}>
            <Col lg={10} xl={10}>
              <h1 className="manrope f-24 w-700" style={{ display: 'flex', alignItems: 'flex-end' }}>Inspiration <span style={{ marginLeft: 4, paddingBottom: 2 }} className="manrope f-20">({images.length})</span></h1>
            </Col>
            <Col lg={14} xl={14} className="justify-in-end">
              <Select
                value={selectedCreatedBy !== undefined ? (selectedCreatedBy || "all") : undefined}
                onChange={(value) => {
                  setSelectedCreatedBy(value === "all" ? "all" : value);
                }}
                placeholder="Created by username"
                style={{
                  width: 180,
                  height: 40,
                  marginRight: 8
                }}
                className="ai-images-dropdown"
                optionLabelProp="label"
              >
                <Select.Option value="all" label="Created by All">
                  All
                </Select.Option>
                {distinctUsernames.sort((a, b) => {
                  if (a === CUSTOMER_USERNAME) return -1;
                  if (b === CUSTOMER_USERNAME) return 1;
                  return a.localeCompare(b);
                }).map((username) => (
                  <Select.Option
                    key={username}
                    value={username}
                    label={`Created by ${username === CUSTOMER_USERNAME ? 'You' : username}`}
                  >
                    {username === CUSTOMER_USERNAME ? 'You' : username}
                  </Select.Option>
                ))}
              </Select>
              <div className="search-bar-width mr-8">
                <Form>
                  <Input.Group compact size="large" className="d-inline">
                    <Input
                      placeholder="Search All Inspirations"
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      className="manrope f-14 mat-search-bar"
                    />
                  </Input.Group>
                </Form>
              </div>
            
          </Col>
        </Row>

        <Row justify="center" align="middle" style={{ marginTop: "5%" }}>
          <Col span={24} style={{ textAlign: "center" }}>
            <ImgOutline />
            <span className="manrope f-20 mt-24" style={{ display: "block" }}>
              {searchValue.trim() ? "No inspirations found matching your search" : "No inspiration images in your library yet"}
            </span>
            {!searchValue.trim() && (
              <div className="mt-16" style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddInspirationClick}
                  className="browse-previews-btn"
                >
                  Add Inspiration
                </Button>
              </div>
            )}
          </Col>
        </Row>
        </div>

        {/* Create/Upload Inspiration Modal */}
        <CreateProductModal
          visible={createInspirationModalVisible}
          onOk={handleCreateInspirationModalOk}
          onCancel={handleCreateInspirationModalCancel}
          mode="prop"
          propsLibrary={[]}
          showNameField={true}
          showCategoryField={true}
          nameFieldLabel="Name"
          nameFieldPlaceholder="Enter name"
          showDimensions={false}
          modalTitle="Add Inspiration"
          showBulkUploadTab={true}
          bulkUploadType="inspiration"
          onBulkUploadSubmit={handleBulkInspirationUpload}
        />
        </div>
      </CustomerMainLayout>
    );
  }

  return (
    <CustomerMainLayout selected="36">
      <div style={{ position: 'relative' }}>
        {addInspirationLoader && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <AiLoader />
          </div>
        )}
        <div style={{ padding: "20px" }}>
          <Row className="justify-space-between" style={{ marginBottom: "20px" }}>
            <Col lg={10} xl={10}>
              <h1 className="manrope f-24 w-700" style={{ display: 'flex', alignItems: 'flex-end' }}>Inspiration <span style={{ marginLeft: 4, paddingBottom: 2 }} className="manrope f-20">({images.length})</span></h1>
            </Col >
            <Col lg={14} xl={14} className="justify-in-end">
              {displayedImages.length > 0 && (
                <>
                  <Select
                    value={selectedCreatedBy !== undefined ? (selectedCreatedBy || "all") : undefined}
                    onChange={(value) => {
                      setSelectedCreatedBy(value === "all" ? "all" : value);
                    }}
                    placeholder="Created by username"
                    style={{
                      width: 180,
                      height: 40,
                      marginRight: 8
                    }}
                    className="ai-images-dropdown"
                    optionLabelProp="label"
                  >
                    <Select.Option value="all" label="Created by All">
                      All
                    </Select.Option>
                    {distinctUsernames.sort((a, b) => {
                      if (a === CUSTOMER_USERNAME) return -1;
                      if (b === CUSTOMER_USERNAME) return 1;
                      return a.localeCompare(b);
                    }).map((username) => (
                      <Select.Option
                        key={username}
                        value={username}
                        label={`Created by ${username === CUSTOMER_USERNAME ? 'You' : username}`}
                      >
                        {username === CUSTOMER_USERNAME ? 'You' : username}
                      </Select.Option>
                    ))}
                  </Select>
                  <div className="search-bar-width mr-8">
                    <Form>
                      <Input.Group compact size="large" className="d-inline">
                        <Input
                          placeholder="Search All Inspirations"
                          value={searchValue}
                          onChange={(e) => setSearchValue(e.target.value)}
                          className="manrope f-14 mat-search-bar"
                        />
                      </Input.Group>
                    </Form>
                  </div>
                </>
              )}
              <Button className='new-product-btn black f-14' onClick={handleAddInspirationClick}>
                <PlusOutlined />Add Inspiration
              </Button>
            </Col>
          </Row >

          <div
            ref={galleryRef}
            onScroll={handleScroll}
            style={{ overflowY: "auto", maxHeight: "calc(100vh - 180px)", overflowX: "hidden" }}
          >
            <Row gutter={[16, 16]}>
              {displayedImages.map((item, index) => {
                const identifier = getIdentifier(item);

                return (
                  <Col xs={24} sm={12} md={8} lg={6} xl={6} key={item.id || index}>
                    <Card
                      onClick={(e) => handleImageClick(index, e)}
                      className="prop-card"
                      bodyStyle={{ padding: 0 }}
                    >
                      <div className="prop-card-wrapper">
                        <Dropdown
                          overlay={getMenuOptions(item)}
                          trigger={['click']}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="prop-more-icon" onClick={(e) => e.stopPropagation()}>
                            <img src={"/img/more.svg"} />
                          </div>
                        </Dropdown>
                        <img
                          src={item.thumbnail || item.src}
                          alt={identifier}
                          className="prop-img img-inspiration"
                        />

                        {identifier && (
                          <div className="prop-identifier-overlay">
                            <span className="prop-identifier-text">{identifier}</span>
                          </div>
                        )}
                      </div>
                    </Card>
                  </Col>
                )
              })}
            </Row>
          </div>


          {
            selectedImage && (
              <Image
                style={{ display: 'none' }}
                src={selectedImage.src}
                preview={{
                  visible: isModalVisible,
                  onVisibleChange: (visible) => {
                    setIsModalVisible(visible);
                    if (!visible) {
                      setSelectedImage(null);
                    }
                  },
                  getContainer: () => document.body
                }}
              />
            )
          }


          {/* Delete Confirmation Modal */}
          <DeleteEntityModal
            onCancel={() => {
              setDeleteModalVisible(false);
              setItemToDelete(null);
              isDeletingRef.current = false;
            }}
            visible={deleteModalVisible}
            deletingModelLoader={deleteImageLoader}
            selected_id={itemToDelete?.id}
            delete_model={confirmDelete}
            heading={'Delete inspiration'}
            subText={'Are you sure you want to delete this inspiration image?'}
          />

          {/* Edit Modal */}
          <EditInspirationModal
            visible={editModalVisible}
            onCancel={handleEditCancel}
            onSuccess={handleEditSuccess}
            itemToEdit={itemToEdit}
          />

        {/* Create/Upload Inspiration Modal */}
        <CreateProductModal
          visible={createInspirationModalVisible}
          onOk={handleCreateInspirationModalOk}
          onCancel={handleCreateInspirationModalCancel}
          mode="prop"
          propsLibrary={[]}
          showNameField={true}
          showCategoryField={true}
          nameFieldLabel="Name"
          nameFieldPlaceholder="Enter name"
          showDimensions={false}
          modalTitle="Add Inspiration"
          showBulkUploadTab={true}
          bulkUploadType="inspiration"
          onBulkUploadSubmit={handleBulkInspirationUpload}
        />
      </div>
      </div>
    </CustomerMainLayout>
  );
};

export default InspirationLibrary;
